import type { ResetPasswordSchema } from '@repo/schemas/auth/resetPassword';

import { usePostPasswordReset } from '@repo/api/auth/usePostPasswordReset';
import { cn } from '@repo/lib/cn';
import { AtSign } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group';
import { useIsDefaultLocation } from '@/hooks/default-location';
import { usePasswordResetForm } from '@/hooks/password-reset-form';

export function PasswordResetForm() {
  const navigate = useNavigate();
  const { mutateAsync, isPending, isSuccess } = usePostPasswordReset();
  const { form, onSubmit } = usePasswordResetForm(mutateAsync);
  const email = useWatch<ResetPasswordSchema>({ control: form.control, name: 'email' });
  const isDefaultLocation: boolean = useIsDefaultLocation();

  return (
    <Form {...form}>
      <form className={'flex h-full w-full grow flex-col gap-6'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <header className={'flex w-full flex-col items-center gap-4 py-1 text-center'}>
          <img src={'/logo.png'} alt={'Better Call Paul logo'} className={'size-12 object-contain drop-shadow-[0_0_60px_var(--color-brand-accent-hover)]'} />
          <span aria-hidden className="bg-brand-accent/85 dark:bg-brand-accent/75 absolute -top-24 -z-10 size-60 rounded-full blur-2xl" />
          <div className={'flex w-full flex-col gap-1'}>
            <h1 className={'text-foreground text-2xl font-semibold'}>Resetuj Hasło</h1>
            <p className={'text-muted-foreground text-sm'}>
              {isSuccess ? (
                <>
                  Link do resetu hasła został wysłany na adres email <strong>{email}</strong>
                </>
              ) : (
                'Wprowadź adres email, na który zostanie wysłany link do resetu hasła.'
              )}
            </p>
          </div>
        </header>
        <fieldset className={'flex w-full flex-col gap-0'}>
          <FormField
            name={'email'}
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem className={'gap-1'}>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupAddon align="inline-start">
                      <InputGroupText>
                        <AtSign />
                      </InputGroupText>
                    </InputGroupAddon>
                    <InputGroupInput
                      disabled={field.disabled || isSuccess}
                      {...field}
                      aria-invalid={fieldState.invalid || undefined}
                      placeholder={'Email'}
                      autoCapitalize={'none'}
                      autoComplete={'email'}
                    />
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
        <footer className={'mt-auto flex flex-row gap-3'}>
          <Button
            name="back button"
            type={'button'}
            variant={'outline'}
            className={cn(isDefaultLocation ? 'hidden' : 'w-full')}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cofnij
          </Button>
          {isSuccess ? (
            <Button asChild disabled={isPending} type={'button'} variant={'primary'} className={'w-full'}>
              <Link to={'/login'}>Zaloguj się</Link>
            </Button>
          ) : (
            <Button disabled={isPending || isSuccess} type={'submit'} variant={'primary'} className={'w-full'}>
              {isPending ? 'Wysyłanie...' : 'Wyślij'}
            </Button>
          )}
        </footer>
      </form>
    </Form>
  );
}
